import React, { useEffect, useState } from "react";
import { X, Download, Share } from "lucide-react";
import { Button } from "./ui/button";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect mobile device
    const userAgent = navigator.userAgent || navigator.vendor;
    const mobileCheck = /android|iphone|ipad|ipod/i.test(userAgent.toLowerCase());
    setIsMobile(mobileCheck);

    // Detect iOS
    const iosCheck = /iphone|ipad|ipod/i.test(userAgent.toLowerCase());
    setIsIOS(iosCheck);

    console.log('[PWA] Device detection - Mobile:', mobileCheck, 'iOS:', iosCheck, 'Android:', mobileCheck && !iosCheck);

    // Check if already installed (standalone mode)
    const isStandalone = window.matchMedia(
      "(display-mode: standalone)"
    ).matches;
    const isInStandaloneMode = (window.navigator as any).standalone === true || isStandalone;

    console.log('[PWA] Install status - Standalone:', isStandalone, 'In standalone mode:', isInStandaloneMode);

    if (isInStandaloneMode) {
      console.log('[PWA] App already installed, not showing prompt');
      return; // Already installed, don't show prompt
    }

    // Check if user dismissed recently (within 7 days)
    const dismissedTime = localStorage.getItem("pwa-install-dismissed");
    if (dismissedTime) {
      const daysSinceDismiss =
        (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60 * 24);
      if (daysSinceDismiss < 7) {
        return; // Don't show if dismissed within last 7 days
      }
    }

    // Track visits for smart timing
    const visits = parseInt(localStorage.getItem("pwa-install-visits") || "0");
    localStorage.setItem("pwa-install-visits", (visits + 1).toString());

    // Show prompt based on engagement
    if (mobileCheck) {
      if (visits >= 1) {
        // Show after 2nd visit
        setTimeout(() => setShowPrompt(true), 3000); // Wait 3 seconds
      } else {
        // On first visit, show after 30 seconds of engagement
        setTimeout(() => setShowPrompt(true), 30000);
      }
    }

    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('[PWA] beforeinstallprompt event fired!');
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    // Debug: Log if service worker is registered
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.ready.then((registration) => {
        console.log('[PWA] Service Worker is ready', registration);

        // Check for updates
        registration.update().then(() => {
          console.log('[PWA] Checked for service worker updates');
        });
      }).catch((error) => {
        console.error('[PWA] Service Worker registration failed', error);
      });

      navigator.serviceWorker.getRegistration().then((registration) => {
        if (registration) {
          console.log('[PWA] Service Worker registered at scope:', registration.scope);

          // Expose cache clear function for debugging
          (window as any).clearPWACache = async () => {
            const cacheNames = await caches.keys();
            await Promise.all(cacheNames.map(name => caches.delete(name)));
            console.log('[PWA] All caches cleared. Reload the page.');
          };
          console.log('[PWA] Debug: Run window.clearPWACache() to clear all caches');
        } else {
          console.warn('[PWA] No Service Worker registration found');
        }
      });
    } else {
      console.warn('[PWA] Service Worker not supported in this browser');
    }

    // Check manifest
    const manifestLink = document.querySelector('link[rel="manifest"]');
    if (manifestLink) {
      console.log('[PWA] Manifest link found:', manifestLink.getAttribute('href'));
    } else {
      console.warn('[PWA] No manifest link found in HTML');
    }

    // Additional debugging for why beforeinstallprompt might not fire
    console.log('[PWA] Window location:', window.location.href);
    console.log('[PWA] Protocol:', window.location.protocol, '(must be https in production)');

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, []);

  const handleInstallClick = async () => {
    console.log('[PWA] Install clicked. isIOS:', isIOS, 'deferredPrompt:', !!deferredPrompt);
    if (isIOS) {
      // Show iOS instructions
      setShowIOSInstructions(true);
    } else if (deferredPrompt) {
      // Android/Chrome install with native prompt
      console.log('[PWA] Showing native install prompt');
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log('[PWA] User choice:', outcome);
      if (outcome === "accepted") {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    } else {
      // Fallback: Show manual installation instructions for Android
      console.warn('[PWA] No deferred prompt available. Showing manual instructions.');
      setShowIOSInstructions(true); // Reuse the modal for Android instructions
    }
  };

  const handleDismiss = () => {
    localStorage.setItem("pwa-install-dismissed", Date.now().toString());
    setShowPrompt(false);
    setShowIOSInstructions(false);
  };

  if (!isMobile || !showPrompt) {
    return null;
  }

  return (
    <>
      {/* Install Instructions Modal */}
      {showIOSInstructions && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-sm w-full p-6 relative animate-in fade-in slide-in-from-bottom-4">
            <button
              onClick={handleDismiss}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-semibold mb-4">
              Install ClaryNext App
            </h3>

            {isIOS ? (
              // iOS Instructions
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <p>
                    Tap the <Share className="inline w-4 h-4 mx-1" /> Share button
                    in Safari
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <p>Scroll down and tap "Add to Home Screen"</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                    3
                  </div>
                  <p>Tap "Add" to install ClaryNext</p>
                </div>
              </div>
            ) : (
              // Android Instructions
              <div className="space-y-3 text-sm text-gray-600">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                    1
                  </div>
                  <p>
                    Tap the menu button <span className="inline-flex items-center justify-center w-5 h-5 mx-1 text-xs">⋮</span> (three dots) in Chrome
                  </p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                    2
                  </div>
                  <p>Tap "Add to Home screen" or "Install app"</p>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary text-white flex items-center justify-center text-xs font-bold">
                    3
                  </div>
                  <p>Follow the prompts to install ClaryNext</p>
                </div>
                <div className="mt-4 p-3 bg-primary/5 rounded-md text-xs">
                  <p className="font-medium text-primary mb-1">Note:</p>
                  <p className="text-primary">
                    If you don't see the install option, the app may already be installed or you may need to use Chrome browser.
                  </p>
                </div>
              </div>
            )}

            <Button
              onClick={handleDismiss}
              className="w-full mt-6"
              variant="outline"
            >
              Got it
            </Button>
          </div>
        </div>
      )}

      {/* Install Banner */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-lg animate-in slide-in-from-bottom-full">
        <div className="max-w-2xl mx-auto p-4">
          <div className="flex items-center gap-3">
            {/* App Icon */}
            <div className="flex-shrink-0">
              <img
                src="/pwa-logo.png"
                alt="ClaryNext"
                className="w-12 h-12 rounded-lg"
              />
            </div>

            {/* Text Content */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm text-gray-900">
                Install ClaryNext
              </h3>
              <p className="text-xs text-gray-600 mt-0.5">
                Install our app for a better experience
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={handleInstallClick}
                size="sm"
                className="whitespace-nowrap"
              >
                <Download className="w-4 h-4 mr-1" />
                Install
              </Button>
              <button
                onClick={handleDismiss}
                className="text-gray-400 hover:text-gray-600 p-1"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstallPrompt;
